import axios from 'axios';

/** CRA inlines REACT_APP_* at build time; trim and strip trailing slash. If base ends with /api, strip it so paths like /api/login are not doubled. */
function normalizeApiBaseUrl(raw: string | undefined): string {
  let base = (raw || 'https:tlcxkba.com').trim();
  // Strip surrounding quotes if .env was mis-typed as KEY="https://..."
  if ((base.startsWith('"') && base.endsWith('"')) || (base.startsWith("'") && base.endsWith("'"))) {
    base = base.slice(1, -1).trim();
  }
  base = base.replace(/\/$/, '');
  if (base.endsWith('/api')) {
    base = base.slice(0, -4);
  }
  return base;
}

const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL);

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor to handle FormData - remove Content-Type so browser can set it with boundary
api.interceptors.request.use(
  (config) => {
    const method = (config.method ?? 'get').toLowerCase();
    const hasJsonBody = config.data !== undefined && config.data !== null && !(config.data instanceof FormData);

    // Content-Type: application/json on GET triggers a CORS preflight; only set for requests with a JSON body.
    if (hasJsonBody && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    // If the data is FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    } else if (!hasJsonBody && (method === 'get' || method === 'head')) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global response interceptor to handle session expiration and update session expiry
api.interceptors.response.use(
  (response) => {
    // Update session expiry if provided in response
    if (response.data?.session_expires_at) {
      // Store session expiry in sessionStorage for persistence across page reloads
      sessionStorage.setItem('session_expires_at', response.data.session_expires_at);
    }
    return response;
  },
  (error) => {
    // If we get a 401 and we're not already on the login page, handle session expiration
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      // Clear session expiry from storage
      sessionStorage.removeItem('session_expires_at');
      
      // Dispatch custom event for session expiration
      window.dispatchEvent(new CustomEvent('session-expired'));
      
      // Clear any stale state and redirect
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const uploadAvatar = async (file: File) => {
  const formData = new FormData();
  formData.append('avatar', file);

  const res = await api.post('/api/user/avatar', formData);

  return res.data.user;
};

export const updateUserProfile = async (data: { phone_number?: string; email?: string }) => {
  const res = await api.put('/api/user/profile', data);
  return res.data.user;
};

export const removeAvatar = async () => {
  const res = await api.delete('/api/user/avatar');
  return res.data.user;
};

// Student term profile (boarding status per term)
export interface StudentTermProfileDto {
  id: number;
  student_id: number;
  school: string | null;
  academic_year: string;
  term: number;
  boarding_status: 'day_scholar' | 'boarder';
  admin_override?: boolean;
}

export interface StudentTermProfileResponse {
  profile: StudentTermProfileDto | null;
  boarding_locked?: boolean;
}

export const fetchStudentTermProfile = async (params: {
  academic_year: string;
  term: number;
}): Promise<StudentTermProfileResponse> => {
  const res = await api.get('/api/student/term-profile', { params });
  return res.data as StudentTermProfileResponse;
};

export const saveStudentTermProfile = async (payload: {
  academic_year: string;
  term: number;
  boarding_status: 'day_scholar' | 'boarder';
}): Promise<StudentTermProfileResponse> => {
  const res = await api.post('/api/student/term-profile', payload);
  return res.data as StudentTermProfileResponse;
};

export interface AssignmentResource {
  id: number;
  original_name: string;
  mime_type: string | null;
  size: number | null;
  path: string;
  url: string;
  created_at: string;
  updated_at: string;
}

export const uploadAssignmentResource = async (file: File): Promise<AssignmentResource> => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await api.post('/api/assignment-resources', formData);

  return res.data.resource as AssignmentResource;
};

export const fetchAssignmentResources = async (): Promise<AssignmentResource[]> => {
  const res = await api.get('/api/assignment-resources');
  return res.data.resources as AssignmentResource[];
};

export interface AssignmentDto {
  id: number;
  teacher_id: number;
  teacher_name: string;
  class_name: string | null;
  subject: string;
  title: string;
  due_date: string;
  total_marks: number | null;
  description: string | null;
  resources: AssignmentResource[] | null;
}

export interface CreateAssignmentPayload {
  school?: SchoolCode;
  class_name?: string | null;
  subject: string;
  title: string;
  due_date: string;
  total_marks?: number | null;
  description?: string | null;
  resources?: AssignmentResource[];
}

export interface FetchAssignmentsParams {
  school?: SchoolCode;
}

export const fetchAssignments = async (params?: FetchAssignmentsParams): Promise<AssignmentDto[]> => {
  const res = await api.get('/api/assignments', { params });
  return res.data.assignments as AssignmentDto[];
};

export const createAssignment = async (payload: CreateAssignmentPayload): Promise<AssignmentDto> => {
  const res = await api.post('/api/assignments', payload);
  return res.data.assignment as AssignmentDto;
};

export const updateAssignment = async (id: number, payload: Partial<CreateAssignmentPayload>): Promise<AssignmentDto> => {
  const res = await api.put(`/api/assignments/${id}`, payload);
  return res.data.assignment as AssignmentDto;
};

export const deleteAssignment = async (id: number): Promise<void> => {
  await api.delete(`/api/assignments/${id}`);
};

// Club API functions
export interface ClubDto {
  id: number;
  name: string;
  description?: string | null;
  status: 'active' | 'inactive';
  created_by: number;
  created_at: string;
  updated_at: string;
  members_count?: number;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface ClubMemberDto {
  id: number;
  name: string;
  email: string;
  student_id?: string | null;
  form?: number | null;
  pivot?: {
    role: string;
    joined_at: string;
  };
}

export interface CreateClubPayload {
  name: string;
  description?: string;
  status?: 'active' | 'inactive';
  school?: SchoolCode;
}

export interface FetchClubsParams {
  school?: SchoolCode;
}

export const fetchClubs = async (params?: FetchClubsParams): Promise<ClubDto[]> => {
  const res = await api.get('/api/clubs', { params });
  return res.data as ClubDto[];
};

export const fetchActiveClubs = async (params?: FetchClubsParams): Promise<ClubDto[]> => {
  const res = await api.get('/api/clubs/active', { params });
  return res.data as ClubDto[];
};

export const createClub = async (payload: CreateClubPayload): Promise<{ message: string; club: ClubDto }> => {
  const res = await api.post('/api/clubs', payload);
  return res.data;
};

export const updateClub = async (id: number, payload: Partial<CreateClubPayload>): Promise<{ message: string; club: ClubDto }> => {
  const res = await api.put(`/api/clubs/${id}`, payload);
  return res.data;
};

export const deleteClub = async (id: number): Promise<{ message: string }> => {
  const res = await api.delete(`/api/clubs/${id}`);
  return res.data;
};

export const fetchClubMembers = async (clubId: number): Promise<{ club: ClubDto; members: ClubMemberDto[] }> => {
  const res = await api.get(`/api/clubs/${clubId}/members`);
  return res.data;
};

export const addClubMember = async (clubId: number, userId: number, role?: string): Promise<{ message: string; member: UserDto }> => {
  const res = await api.post(`/api/clubs/${clubId}/members`, { user_id: userId, role });
  return res.data;
};

export const removeClubMember = async (clubId: number, userId: number): Promise<{ message: string }> => {
  const res = await api.delete(`/api/clubs/${clubId}/members/${userId}`);
  return res.data;
};

export const updateClubMemberRole = async (clubId: number, userId: number, role: string): Promise<{ message: string }> => {
  const res = await api.put(`/api/clubs/${clubId}/members/${userId}/role`, { role });
  return res.data;
};

// Announcement API functions
export interface AnnouncementDto {
  id: number;
  title: string;
  message: string;
  target_group: 'student' | 'teacher' | 'admin' | 'all' | 'clubs';
  club_id?: number | null;
  club?: ClubDto | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface CreateAnnouncementPayload {
  title: string;
  message: string;
  target_group: 'student' | 'teacher' | 'admin' | 'all' | 'clubs';
  club_id?: number | null;
  school?: SchoolCode;
  send_email?: boolean;
}

export interface FetchAnnouncementsParams {
  type?: 'regular' | 'club';
  club_id?: number;
  target_group?: 'student' | 'teacher' | 'admin' | 'all' | 'clubs';
  school?: SchoolCode;
}

export const fetchAnnouncements = async (params?: FetchAnnouncementsParams): Promise<AnnouncementDto[]> => {
  const res = await api.get('/api/announcements', { params });
  return res.data as AnnouncementDto[];
};

export interface CreateAnnouncementResponse {
  announcement: AnnouncementDto;
  emails_sent?: number;
  emails_failed?: number;
}

export const createAnnouncement = async (payload: CreateAnnouncementPayload): Promise<CreateAnnouncementResponse> => {
  const res = await api.post('/api/announcements', payload);
  // If response has announcement property, return the full response, otherwise wrap it
  if (res.data.announcement) {
    return res.data as CreateAnnouncementResponse;
  }
  // Legacy format - just the announcement object
  return { announcement: res.data as AnnouncementDto };
};

export const updateAnnouncement = async (id: number, payload: Partial<CreateAnnouncementPayload>): Promise<AnnouncementDto> => {
  const res = await api.put(`/api/announcements/${id}`, payload);
  return res.data as AnnouncementDto;
};

export const deleteAnnouncement = async (id: number): Promise<void> => {
  await api.delete(`/api/announcements/${id}`);
};

// School codes
export type SchoolCode = 'KBA' | 'TLC';

// User Management API functions
export interface UserDto {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string; // System-generated, not user-visible
  login_id?: string; // Auto-generated login ID (KBA T01, KBA S001, KBA Admin01, TLC T01, etc.)
  role: 'student' | 'teacher' | 'admin';
  school?: SchoolCode; // School assignment (KBA or TLC)
  status?: string;
  student_id?: string;
  teacher_id?: string;
  form?: number;
  gender?: string;
  date_of_birth?: string;
  phone_number?: string;
  recovery_email?: string | null;
  announcement_email?: string | null;
  updated_at?: string;
  created_at?: string;
}

export interface CreateUserPayload {
  first_name: string;
  last_name: string;
  role: 'student' | 'teacher' | 'admin';
  school?: SchoolCode; // School selection (KBA or TLC)
  form?: number;
  gender: string;
  date_of_birth: string;
  allow_duplicate?: boolean;
  // Note: student_id and teacher_id are auto-generated by the backend
}

export interface UpdateUserPayload {
  name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
  role?: 'student' | 'teacher' | 'admin';
  student_id?: string;
  teacher_id?: string;
  form?: number;
  gender?: string;
  date_of_birth?: string;
  status?: string;
}

export interface FetchUsersParams {
  school?: SchoolCode;
  role?: 'student' | 'teacher' | 'admin';
  search?: string;
  include_inactive?: boolean;
  status?: 'active' | 'inactive' | 'graduated';
  boarding_status?: 'boarder' | 'day_scholar';
  academic_year?: string;
  term?: number;
}

export const fetchUsers = async (params?: FetchUsersParams): Promise<UserDto[]> => {
  const res = await api.get('/api/admin/users', { params });
  return res.data.users as UserDto[];
};

export const createUser = async (payload: CreateUserPayload): Promise<UserDto> => {
  const res = await api.post('/api/admin/users', payload);
  return res.data.user as UserDto;
};

export const updateUser = async (id: number, payload: UpdateUserPayload): Promise<UserDto> => {
  const res = await api.put(`/api/admin/users/${id}`, payload);
  // Backend returns { user: User }
  return res.data.user as UserDto;
};

export const deleteUser = async (id: number): Promise<{ message: string }> => {
  const res = await api.delete(`/api/admin/users/${id}`);
  return res.data;
};

export const permanentlyDeleteUser = async (id: number): Promise<{ message: string }> => {
  const res = await api.delete(`/api/admin/users/${id}/permanent`);
  return res.data;
};

export const fetchUserById = async (id: number): Promise<UserDto> => {
  const res = await api.get(`/api/admin/users/${id}`);
  return res.data as UserDto;
};

export const fetchNextUserLoginId = async (
  role: 'student' | 'teacher' | 'admin',
  school?: SchoolCode
): Promise<string> => {
  const res = await api.get('/api/admin/users-next-login-id', {
    params: { role, school },
  });
  return res.data?.login_id as string;
};

// Password Reset Request API functions
export interface PasswordResetRequestDto {
  id: number;
  user_id: number;
  login_id: string;
  name: string;
  role: 'student' | 'teacher' | 'admin';
  status: 'pending' | 'approved' | 'rejected';
  reason: string | null;
  approved_by: number | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  user?: UserDto;
  approver?: UserDto;
}

export const fetchPasswordResetRequests = async (): Promise<PasswordResetRequestDto[]> => {
  const res = await api.get('/api/admin/password-reset-requests');
  return res.data.requests as PasswordResetRequestDto[];
};

export const approvePasswordReset = async (id: number): Promise<PasswordResetRequestDto> => {
  const res = await api.post(`/api/admin/password-reset-requests/${id}/approve`);
  return res.data.request as PasswordResetRequestDto;
};

export const rejectPasswordReset = async (id: number): Promise<PasswordResetRequestDto> => {
  const res = await api.post(`/api/admin/password-reset-requests/${id}/reject`);
  return res.data.request as PasswordResetRequestDto;
};

export interface UserPasswordInfo {
  user: UserDto;
  is_default_password: boolean;
  password_change_required: boolean;
}

export const getUserPasswordInfo = async (id: number): Promise<UserPasswordInfo> => {
  const res = await api.get(`/api/admin/users/${id}/password-info`);
  return res.data as UserPasswordInfo;
};

export const resetUserPassword = async (id: number): Promise<UserPasswordInfo> => {
  const res = await api.post(`/api/admin/users/${id}/reset-password`);
  return res.data as UserPasswordInfo;
};

// Subject API functions
export interface SubjectDto {
  id: number;
  name: string;
  code: string;
  // Each subject belongs to a specific school in the backend schema
  school: SchoolCode;
  created_at?: string;
  updated_at?: string;
}

export interface CreateSubjectPayload {
  name: string;
  code: string;
  school?: SchoolCode;
}

export interface FetchSubjectsParams {
  school?: SchoolCode;
}

export const fetchSubjects = async (params?: FetchSubjectsParams): Promise<SubjectDto[]> => {
  const res = await api.get('/api/subjects', { params });
  return res.data as SubjectDto[];
};

export const createSubject = async (payload: CreateSubjectPayload): Promise<SubjectDto> => {
  const res = await api.post('/api/subjects', payload);
  return res.data as SubjectDto;
};

export interface BulkSubjectResponse {
  success: boolean;
  operation_status?: 'full_success' | 'partial_success' | 'failed';
  message: string;
  created: SubjectDto[];
  errors: Array<{ subject: CreateSubjectPayload; error: string }>;
  item_results?: Array<{
    index: number;
    subject: CreateSubjectPayload;
    status: 'created' | 'updated' | 'skipped' | 'failed';
    error?: string;
  }>;
  requested_count?: number;
  succeeded_count?: number;
  failed_count?: number;
}

export const bulkCreateSubjects = async (subjects: CreateSubjectPayload[], school?: SchoolCode): Promise<BulkSubjectResponse> => {
  const res = await api.post('/api/subjects/bulk', { subjects, school });
  return res.data as BulkSubjectResponse;
};

export const clearAllSubjects = async (school?: SchoolCode): Promise<{ message: string }> => {
  const res = await api.post('/api/subjects/clear-all', { school });
  return res.data;
};

// Teacher-Subject Assignment API functions
export interface TeacherSubjectDto {
  id: number;
  teacher_id: number;
  subject_id: number;
  form: number;
  teacher?: {
    id: number;
    name: string;
  };
  subject?: {
    id: number;
    name: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface CreateTeacherSubjectPayload {
  teacher_id: number;
  subject_id: number;
  form: number;
  school?: SchoolCode;
}

export const fetchTeacherSubjects = async (form?: number, teacherId?: number, school?: SchoolCode): Promise<TeacherSubjectDto[]> => {
  const params = new URLSearchParams();
  if (form) params.append('form', form.toString());
  if (teacherId) params.append('teacher_id', teacherId.toString());
  if (school) params.append('school', school);
  const queryString = params.toString();
  const res = await api.get(`/api/teacher-subjects${queryString ? `?${queryString}` : ''}`);
  return res.data as TeacherSubjectDto[];
};

export const createTeacherSubject = async (payload: CreateTeacherSubjectPayload): Promise<TeacherSubjectDto> => {
  const res = await api.post('/api/teacher-subjects', payload);
  return res.data as TeacherSubjectDto;
};

export const updateTeacherSubject = async (id: number, payload: Partial<CreateTeacherSubjectPayload>): Promise<TeacherSubjectDto> => {
  const res = await api.put(`/api/teacher-subjects/${id}`, payload);
  return res.data as TeacherSubjectDto;
};

export const deleteTeacherSubject = async (id: number): Promise<void> => {
  await api.delete(`/api/teacher-subjects/${id}`);
};

export const clearTeacherSubjectsForForm = async (params: {
  school: SchoolCode;
  form: number;
}): Promise<{ message: string; deleted: number }> => {
  const res = await api.post('/api/teacher-subjects/clear-for-form', params);
  return res.data;
};

export interface BulkTeacherSubjectPayload {
  assignments: CreateTeacherSubjectPayload[];
  school?: SchoolCode;
}

export interface BulkTeacherSubjectResponse {
  success: boolean;
  operation_status?: 'full_success' | 'partial_success' | 'failed';
  created: TeacherSubjectDto[];
  updated: TeacherSubjectDto[];
  errors: Array<{ assignment: CreateTeacherSubjectPayload; error: string }>;
  item_results?: Array<{
    index: number;
    assignment: CreateTeacherSubjectPayload;
    status: 'created' | 'updated' | 'skipped' | 'failed';
    error?: string;
  }>;
  requested_count?: number;
  succeeded_count?: number;
  failed_count?: number;
  total_successful?: number;
  unique_subjects?: number;
  message: string;
}

export const bulkSaveTeacherSubjects = async (payload: BulkTeacherSubjectPayload): Promise<BulkTeacherSubjectResponse> => {
  const res = await api.post('/api/teacher-subjects/bulk', payload);
  return res.data as BulkTeacherSubjectResponse;
};

// Audit Log API functions
export interface AuditLogDto {
  id: number;
  user_id: number | null;
  actor: string;
  action: string;
  entity_type: string | null;
  entity_id: number | null;
  details: string | null;
  ip_address: string | null;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface AuditLogFilters {
  date_from?: string;
  date_to?: string;
  user_filter?: string;
  page?: number;
}

export interface PaginatedAuditLogResponse {
  data: AuditLogDto[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export const fetchAuditLogs = async (filters?: AuditLogFilters): Promise<PaginatedAuditLogResponse> => {
  const params = new URLSearchParams();
  if (filters?.date_from) params.append('date_from', filters.date_from);
  if (filters?.date_to) params.append('date_to', filters.date_to);
  if (filters?.user_filter) params.append('user_filter', filters.user_filter);
  // Always include page parameter, default to 1 if not provided
  const page = filters?.page || 1;
  params.append('page', page.toString());
  
  const queryString = params.toString();
  const url = `/api/audit-logs${queryString ? `?${queryString}` : ''}`;
  const res = await api.get(url);
  
  // Validate response structure
  if (!res.data || typeof res.data !== 'object') {
    console.error('Invalid response structure:', res.data);
    throw new Error('Invalid response from server');
  }
  
  // Handle case where API returns array directly (fallback for old API)
  if (Array.isArray(res.data)) {
    return {
      data: res.data,
      current_page: page,
      last_page: 1,
      per_page: res.data.length,
      total: res.data.length
    } as PaginatedAuditLogResponse;
  }
  
  // API should return paginated response with data, current_page, last_page, per_page, total
  return res.data as PaginatedAuditLogResponse;
};

// Grades Analytics API functions
export interface GradeAnalyticsDto {
  term_analytics: Array<{
    term: string;
    current_year: string;
    previous_year: string;
    current_average: number;
    previous_average: number;
    change_percentage: number;
    change_margin: number;
    total_students: number;
  }>;
  top_performers: Array<{
    student_id: number;
    student: UserDto;
    average_marks: number;
    total_subjects: number;
    grades: Array<{
      subject: string;
      marks: number;
      grade: string;
      term: string;
    }>;
  }>;
  worst_performers: Array<{
    student_id: number;
    student: UserDto;
    average_marks: number;
    total_subjects: number;
    grades: Array<{
      subject: string;
      marks: number;
      grade: string;
      term: string;
    }>;
  }>;
  gender_analytics: Array<{
    gender: string;
    average_marks: number;
    total_students: number;
    total_grades: number;
    /** MSCE grades 1–9 as string keys */
    grade_distribution: Record<string, number>;
  }>;
  best_grades: Array<{
    student_id: number;
    student_name: string;
    student_id_display: string;
    subject: string;
    marks: number;
    grade: string;
    term: string;
    form: number | string;
  }>;
  form_analytics: Array<{
    form: number;
    average_marks: number;
    total_students: number;
    total_grades: number;
    pass_rate: number;
  }>;
  subject_analytics: Array<{
    subject_id: number;
    subject_name: string;
    average_marks: number;
    total_students: number;
    total_grades: number;
    pass_rate: number;
  }>;
  year_comparison: {
    current_year: string;
    previous_year: string;
    current_average: number;
    previous_average: number;
    change_percentage: number;
    change_margin: number;
    is_better: boolean;
    current_total_students: number;
    previous_total_students: number;
  };
}

export const fetchGradeAnalytics = async (year?: string, school?: SchoolCode): Promise<GradeAnalyticsDto> => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (school) params.append('school', school);
  const queryString = params.toString();
  const res = await api.get(`/api/grades/analytics${queryString ? `?${queryString}` : ''}`);
  return res.data as GradeAnalyticsDto;
};

// Timetable API functions
export interface TimetableDto {
  id: number;
  form: number;
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
  period: number;
  subject_id: number | null;
  teacher_id: number | null;
  room_id?: number | null;
  room: string | null;
  start_time: string | null;
  end_time: string | null;
  status: 'draft' | 'published';
  subject?: {
    id: number;
    name: string;
    code: string;
  };
  teacher?: {
    id: number;
    name: string;
    email: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface CreateTimetablePayload {
  form: number;
  day: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
  period: number;
  subject_id?: number | null;
  teacher_id?: number | null;
  room_id?: number | null;
  room?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: 'draft' | 'published';
}

export interface TimetableConflict {
  type: 'teacher' | 'class' | 'room';
  message: string;
  conflicting_entry?: TimetableDto;
}

export interface TimetableUpdateResponse {
  timetable: TimetableDto;
  conflicts?: TimetableConflict[];
}

export const fetchTimetables = async (form?: number, teacherId?: number, status?: 'draft' | 'published', school?: SchoolCode): Promise<TimetableDto[]> => {
  const params = new URLSearchParams();
  if (form) params.append('form', form.toString());
  if (teacherId) params.append('teacher_id', teacherId.toString());
  if (status) params.append('status', status);
  if (school) params.append('school', school);
  const queryString = params.toString();
  const res = await api.get(`/api/timetables${queryString ? `?${queryString}` : ''}`);
  return res.data as TimetableDto[];
};

export interface CreateTimetablePayloadWithSchool extends CreateTimetablePayload {
  school?: SchoolCode;
}

export const createTimetable = async (payload: CreateTimetablePayloadWithSchool): Promise<TimetableDto> => {
  const res = await api.post('/api/timetables', payload);
  return res.data as TimetableDto;
};

export const updateTimetable = async (id: number, payload: Partial<CreateTimetablePayload>): Promise<TimetableUpdateResponse> => {
  const res = await api.put(`/api/timetables/${id}`, payload);
  return res.data as TimetableUpdateResponse;
};

export const deleteTimetable = async (id: number): Promise<void> => {
  await api.delete(`/api/timetables/${id}`);
};

export const generateTimetable = async (
  form: number,
  school?: SchoolCode
): Promise<{ message: string; timetables: TimetableDto[] }> => {
  const params = new URLSearchParams();
  if (school) params.append('school', school);
  const qs = params.toString();
  const res = await api.post(`/api/timetables/generate/${form}${qs ? `?${qs}` : ''}`);
  return res.data;
};

export const publishTimetable = async (form: number, school?: SchoolCode): Promise<{ message: string }> => {
  const params = new URLSearchParams();
  if (school) params.append('school', school);
  const qs = params.toString();
  const res = await api.post(`/api/timetables/publish/${form}${qs ? `?${qs}` : ''}`);
  return res.data;
};

export const clearTimetable = async (form: number, school?: SchoolCode): Promise<{ message: string }> => {
  const params = new URLSearchParams();
  if (school) params.append('school', school);
  const qs = params.toString();
  const res = await api.delete(`/api/timetables/clear/${form}${qs ? `?${qs}` : ''}`);
  return res.data;
};

// Grade API functions
export interface GradeDto {
  id: number;
  student_id: number;
  subject_id: number;
  assignment_id?: number | null;
  marks: number;
  grade: string;
  position: number;
  teacher_remarks?: string | null;
  term: string;
  published?: boolean | number; // Can be boolean (true/false) or number (1/0) from MySQL
  submitted_to_admin?: boolean | number; // Track if teacher submitted to admin
  admin_published?: boolean | number; // Track if admin published to students
  submitted_at?: string | null;
  admin_published_at?: string | null;
  student?: UserDto;
  subject?: SubjectDto;
  assignment?: {
    id: number;
    title: string;
    subject: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface CreateGradePayload {
  student_id: number;
  subject_id: number;
  assignment_id?: number | null;
  marks: number;
  grade: string;
  position: number;
  teacher_remarks?: string | null;
  term: string;
  published?: boolean;
}

export interface ExamGradeSummaryDto {
  term: string;
  deduped_average_marks: number | null;
  class_position: number | null;
  class_size: number;
  msce_best_six_points_sum: number | null;
}

export const fetchExamGradeSummary = async (term: string): Promise<ExamGradeSummaryDto> => {
  const res = await api.get('/api/grades/exam-summary', { params: { term } });
  return res.data as ExamGradeSummaryDto;
};

export const fetchGrades = async (
  form?: number,
  studentId?: number,
  term?: string,
  assignmentId?: number,
  subjectId?: number,
  school?: SchoolCode
): Promise<GradeDto[]> => {
  const params = new URLSearchParams();
  if (form) params.append('form', form.toString());
  if (studentId) params.append('student_id', studentId.toString());
  if (term) params.append('term', term);
  if (assignmentId) params.append('assignment_id', assignmentId.toString());
  if (subjectId) params.append('subject_id', subjectId.toString());
  if (school) params.append('school', school);
  const queryString = params.toString();
  const res = await api.get(`/api/grades${queryString ? `?${queryString}` : ''}`);
  return res.data as GradeDto[];
};

export const fetchClassStudents = async (form: number, subjectId: number, school?: SchoolCode): Promise<UserDto[]> => {
  const params = new URLSearchParams();
  params.append('form', form.toString());
  params.append('subject_id', subjectId.toString());
  if (school) params.append('school', school);
  const res = await api.get(`/api/grades/class-students?${params.toString()}`);
  return res.data.students as UserDto[];
};

export const createGrade = async (payload: CreateGradePayload): Promise<GradeDto> => {
  const res = await api.post('/api/grades', payload);
  return res.data as GradeDto;
};

export const updateGrade = async (id: number, payload: Partial<CreateGradePayload>): Promise<GradeDto> => {
  const res = await api.put(`/api/grades/${id}`, payload);
  return res.data as GradeDto;
};

export const deleteGrade = async (id: number): Promise<void> => {
  await api.delete(`/api/grades/${id}`);
};

export const publishAssignmentGrades = async (assignmentId: number): Promise<{ message: string; updated_count: number }> => {
  const response = await api.post(`/api/grades/publish-assignment/${assignmentId}`);
  return response.data;
};

export interface PublishExamGradesPayload {
  subject_id: number;
  form: number;
  term: string;
  school: SchoolCode;
}

export const publishExamGrades = async (payload: PublishExamGradesPayload): Promise<{ message: string; updated_count: number }> => {
  const response = await api.post('/api/grades/publish-exam', payload);
  return response.data;
};

// Grade Review Workflow APIs

export interface SubmitToAdminPayload {
  subject_id: number;
  form: number;
  term: string;
  school: SchoolCode;
}

export const submitGradesToAdmin = async (payload: SubmitToAdminPayload): Promise<{ message: string; updated_count: number }> => {
  const response = await api.post('/api/grades/submit-to-admin', payload);
  return response.data;
};

export interface GradeSubmissionStatus {
  form: number;
  term: string;
  total_subjects: number;
  submitted_subjects: number;
  published_subjects: number;
  subjects: Array<{
    subject_id: number;
    subject_name: string;
    subject_code: string;
    teacher_id: number;
    teacher_name: string;
    total_students: number;
    saved_grades: number;
    submitted_to_admin: boolean;
    admin_published: boolean;
    submitted_at: string | null;
    admin_published_at: string | null;
  }>;
}

export const getGradeSubmissionStatus = async (form: number, term: string, school?: SchoolCode): Promise<GradeSubmissionStatus> => {
  const response = await api.get('/api/grades/submission-status', {
    params: { form, term, school },
  });
  return response.data;
};

export interface PublishToStudentsPayload {
  form: number;
  term: string;
  school: SchoolCode;
  subject_id?: number; // Optional: publish specific subject or all
}

export const publishGradesToStudents = async (payload: PublishToStudentsPayload): Promise<{ message: string; updated_count: number }> => {
  const response = await api.post('/api/grades/publish-to-students', payload);
  return response.data;
};

export interface RecallSubmissionPayload {
  subject_id: number;
  form: number;
  term: string;
  school?: SchoolCode;
}

export const recallGradeSubmission = async (
  payload: RecallSubmissionPayload
): Promise<{ message: string; updated_count: number }> => {
  const response = await api.post('/api/grades/recall-submission', payload);
  return response.data;
};

export interface UnpublishFromStudentsPayload {
  subject_id: number;
  form: number;
  term: string;
  school?: SchoolCode;
}

export const unpublishGradesFromStudents = async (
  payload: UnpublishFromStudentsPayload
): Promise<{ message: string; updated_count: number }> => {
  const response = await api.post('/api/grades/unpublish-from-students', payload);
  return response.data;
};

export interface ResetExamSubjectPayload {
  subject_id: number;
  form: number;
  term: string;
  school?: SchoolCode;
}

export const resetExamSubjectGrades = async (
  payload: ResetExamSubjectPayload
): Promise<{ message: string; deleted_count: number; matched_count: number }> => {
  // POST, not DELETE: DELETE request bodies can be stripped by shared-host proxies.
  const response = await api.post('/api/grades/reset-exam-subject', payload);
  return response.data;
};

// Class Management API functions
export interface SchoolClassDto {
  id: number;
  name: string;
  short_name: string | null;
  level: number;
  school?: SchoolCode; // School assignment (KBA or TLC)
  description: string | null;
  status: 'active' | 'inactive';
  students_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreateSchoolClassPayload {
  name: string;
  short_name?: string;
  level: number;
  school?: SchoolCode;
  description?: string;
  status?: 'active' | 'inactive';
}

export interface FetchClassesParams {
  school?: SchoolCode;
}

export const fetchClasses = async (params?: FetchClassesParams): Promise<SchoolClassDto[]> => {
  const res = await api.get('/api/classes', { params });
  return res.data as SchoolClassDto[];
};

export const fetchActiveClasses = async (params?: FetchClassesParams): Promise<SchoolClassDto[]> => {
  const res = await api.get('/api/classes/active', { params });
  return res.data as SchoolClassDto[];
};

export const createClass = async (payload: CreateSchoolClassPayload): Promise<{ message: string; class: SchoolClassDto }> => {
  const res = await api.post('/api/classes', payload);
  return res.data;
};

export const updateClass = async (id: number, payload: Partial<CreateSchoolClassPayload>): Promise<{ message: string; class: SchoolClassDto }> => {
  const res = await api.put(`/api/classes/${id}`, payload);
  return res.data;
};

export const deleteClass = async (id: number): Promise<{ message: string }> => {
  const res = await api.delete(`/api/classes/${id}`);
  return res.data;
};

export const reorderClasses = async (
  classes: { id: number; level: number }[],
  school: SchoolCode
): Promise<{ message: string }> => {
  const res = await api.post('/api/classes/reorder', { classes, school });
  return res.data;
};

// Class student management (for admin)
export interface ClassStudentDto {
  id: number;
  name: string;
  student_id?: string | null;
  login_id?: string | null;
  gender?: string | null;
  form?: number | null;
  school?: SchoolCode | null;
  status?: string | null;
}

export interface ClassStudentsResponse {
  class: SchoolClassDto;
  students: ClassStudentDto[];
}

export const fetchClassStudentsByClassId = async (classId: number, params?: { search?: string }): Promise<ClassStudentsResponse> => {
  const res = await api.get(`/api/classes/${classId}/students`, { params });
  return res.data as ClassStudentsResponse;
};

export interface BulkProgressStudentsPayload {
  student_ids: number[];
  action: 'promote' | 'repeat' | 'move';
  target_class_id?: number | null;
}

export const bulkProgressStudents = async (payload: BulkProgressStudentsPayload): Promise<{
  message: string;
  action: string;
  affected: number;
  skipped_student_ids: number[];
}> => {
  const res = await api.post('/api/admin/students/bulk-progress', payload);
  return res.data;
};

// Class Announcement API functions (for teachers)
export interface ClassAnnouncementDto {
  id: number;
  school: SchoolCode;
  title: string;
  message: string;
  form: number;
  email_sent: boolean;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface AssignedClassDto {
  form: number;
  subjects: string[];
  // Optional list of students assigned to this class (when provided by backend)
  students?: {
    id: number;
    name: string;
  }[];
}

export interface CreateClassAnnouncementPayload {
  title: string;
  message: string;
  form: number;
  school?: SchoolCode;
  send_email?: boolean;
}

export interface CreateClassAnnouncementResponse {
  announcement: ClassAnnouncementDto;
  emails_sent: number;
  emails_failed: number;
}

export interface FetchClassAnnouncementsParams {
  school?: SchoolCode;
}

export const fetchClassAnnouncements = async (params?: FetchClassAnnouncementsParams): Promise<ClassAnnouncementDto[]> => {
  const res = await api.get('/api/class-announcements', { params });
  return res.data as ClassAnnouncementDto[];
};

export const fetchClassAnnouncementsForStudent = async (): Promise<ClassAnnouncementDto[]> => {
  const res = await api.get('/api/class-announcements/student');
  return res.data as ClassAnnouncementDto[];
};

export const fetchAssignedClasses = async (school?: SchoolCode): Promise<AssignedClassDto[]> => {
  const params = school ? { school } : undefined;
  const res = await api.get('/api/class-announcements/assigned-classes', { params });
  return res.data as AssignedClassDto[];
};

export const createClassAnnouncement = async (payload: CreateClassAnnouncementPayload): Promise<CreateClassAnnouncementResponse> => {
  const res = await api.post('/api/class-announcements', payload);
  return res.data as CreateClassAnnouncementResponse;
};

export const updateClassAnnouncement = async (id: number, payload: Partial<CreateClassAnnouncementPayload>): Promise<ClassAnnouncementDto> => {
  const res = await api.put(`/api/class-announcements/${id}`, payload);
  return res.data as ClassAnnouncementDto;
};

export const deleteClassAnnouncement = async (id: number): Promise<void> => {
  await api.delete(`/api/class-announcements/${id}`);
};

// ─── Fee Management ────────────────────────────────────────────────

export interface FeeStructureDto {
  id: number;
  school: string;
  academic_year: string;
  term: number;
  form: number;
  fee_type: 'boarder' | 'day_scholar';
  amount: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface StudentFeeAccountDto {
  id: number;
  student_id: number;
  school: string;
  academic_year: string;
  term: number;
  total_fee: string;
  amount_paid: string;
  balance: string;
  carry_over_credit: string;
  opening_balance?: string;
  created_at: string;
  updated_at: string;
}

export interface FeePaymentDto {
  id: number;
  student_id: number;
  school: string;
  academic_year: string;
  term: number;
  receipt_number: string;
  amount: string;
  payment_date: string;
  recorded_by: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  account_balance?: string;
  student?: UserDto;
  recorder?: UserDto;
}

export interface FeeReportDto {
  total_expected: number;
  total_collected: number;
  total_outstanding: number;
  total_accounts: number;
  fully_paid: number;
  fallback_count: number;
  students: Array<{
    student_id: number;
    name: string;
    admission_id: string | null;
    form: number | null;
    accommodation_type: 'day_scholar' | 'boarder' | string;
    used_fallback: boolean;
    fallback_note: string | null;
    opening_balance?: number;
    expected: number;
    paid: number;
    outstanding: number;
    status: 'owes' | 'fully_paid' | 'overpaid';
  }>;
  school_breakdown: Array<{
    school: string;
    total_expected: number;
    total_collected: number;
    total_students: number;
    fully_paid: number;
  }>;
  filters: {
    school: string | null;
    academic_year: string;
    term: number | null;
  };
}

export interface FeeAccessStatusDto {
  school: string;
  has_fees_password: boolean;
  unlocked: boolean;
  can_use_master_reset: boolean;
  next_master_reset_after: string | null;
}

export const fetchFeeAccessStatus = async (school: SchoolCode): Promise<FeeAccessStatusDto> => {
  const res = await api.get('/api/fees/access/status', { params: { school } });
  return res.data as FeeAccessStatusDto;
};

export const unlockFeeSubsystem = async (school: SchoolCode, fees_password: string): Promise<{ message: string; unlocked: boolean }> => {
  const res = await api.post('/api/fees/access/unlock', { school, fees_password });
  return res.data;
};

export const setFeeSubsystemPassword = async (payload: {
  school: SchoolCode;
  master_password: string;
  new_fees_password: string;
}): Promise<{ message: string; unlocked: boolean }> => {
  const res = await api.post('/api/fees/access/set-password', payload);
  return res.data;
};

export interface CrossSchoolAccessStatusDto {
  unlocked: boolean;
  expires_at: string | null;
  ttl_minutes: number;
}

export const fetchCrossSchoolAccessStatus = async (): Promise<CrossSchoolAccessStatusDto> => {
  const res = await api.get('/api/admin/cross-school/status');
  return res.data as CrossSchoolAccessStatusDto;
};

export const unlockCrossSchoolAccess = async (
  unlock_key: string,
): Promise<{ message: string; unlocked: boolean; expires_at?: string }> => {
  const res = await api.post('/api/admin/cross-school/unlock', { unlock_key });
  return res.data;
};

export interface CreateFeeStructurePayload {
  school: string;
  academic_year: string;
  term: number;
  form?: number;
  forms?: number[];
  target_scope?: 'single' | 'multiple' | 'all';
  fee_type: 'boarder' | 'day_scholar';
  amount: number;
  description?: string;
}

export interface CreateFeeStructureResponse {
  message: string;
  target_scope: 'single' | 'multiple' | 'all';
  applied_forms: number[];
  updated_count: number;
  structures: FeeStructureDto[];
}

export interface RecordPaymentPayload {
  student_id: number;
  receipt_number: string;
  amount: number;
  payment_date: string;
  term: number;
  academic_year: string;
  notes?: string;
}

export interface UpdateFeePaymentPayload {
  receipt_number: string;
  amount: number;
  payment_date: string;
  notes?: string;
}

export const fetchFeeStructures = async (params?: {
  school?: string;
  academic_year?: string;
  term?: number;
  form?: number;
}): Promise<FeeStructureDto[]> => {
  const res = await api.get('/api/fees/structures', { params });
  return res.data as FeeStructureDto[];
};

export const createFeeStructure = async (payload: CreateFeeStructurePayload): Promise<CreateFeeStructureResponse> => {
  const res = await api.post('/api/fees/structures', payload);
  return res.data as CreateFeeStructureResponse;
};

export interface UpdateFeeStructurePayload {
  academic_year: string;
  term: number;
  form: number;
  fee_type: 'boarder' | 'day_scholar';
  amount: number;
  description?: string;
}

export const updateFeeStructure = async (id: number, payload: UpdateFeeStructurePayload, school?: SchoolCode): Promise<FeeStructureDto> => {
  const res = await api.put(`/api/fees/structures/${id}`, payload, {
    params: school ? { school } : undefined,
  });
  return res.data as FeeStructureDto;
};

export const deleteFeeStructure = async (id: number, school?: SchoolCode): Promise<{ message: string }> => {
  const res = await api.delete(`/api/fees/structures/${id}`, {
    params: school ? { school } : undefined,
  });
  return res.data as { message: string };
};

export const fetchStudentFees = async (studentId: number, school?: SchoolCode): Promise<{
  student: UserDto;
  accounts: StudentFeeAccountDto[];
  payments: FeePaymentDto[];
}> => {
  const res = await api.get(`/api/fees/student/${studentId}`, {
    params: school ? { school } : undefined,
  });
  return res.data;
};

export const fetchMyFees = async (): Promise<{
  accounts: StudentFeeAccountDto[];
  payments: FeePaymentDto[];
}> => {
  const res = await api.get('/api/fees/my-fees');
  return res.data;
};

export const recordFeePayment = async (payload: RecordPaymentPayload): Promise<{
  message: string;
  payment: FeePaymentDto;
  account: StudentFeeAccountDto;
  carry_over_applied?: { term: number; year: string; credit: number } | null;
}> => {
  const res = await api.post('/api/fees/payments', payload);
  return res.data;
};

export const updateFeePayment = async (
  id: number,
  payload: UpdateFeePaymentPayload,
  school?: SchoolCode
): Promise<{
  message: string;
  payment: FeePaymentDto;
  account: StudentFeeAccountDto;
  carry_over_applied?: { term: number; year: string; credit: number } | null;
}> => {
  const res = await api.put(`/api/fees/payments/${id}`, payload, {
    params: school ? { school } : undefined,
  });
  return res.data;
};

export const deleteFeePayment = async (
  id: number,
  school?: SchoolCode
): Promise<{
  message: string;
  account: StudentFeeAccountDto;
  carry_over_applied?: { term: number; year: string; credit: number } | null;
}> => {
  const res = await api.delete(`/api/fees/payments/${id}`, {
    params: school ? { school } : undefined,
  });
  return res.data;
};

export const fetchFeePayments = async (params?: {
  school?: string;
  academic_year?: string;
  term?: number;
  student_id?: number;
  search?: string;
  form?: number;
}): Promise<FeePaymentDto[]> => {
  const res = await api.get('/api/fees/payments', { params });
  return res.data as FeePaymentDto[];
};

export const checkReceiptNumber = async (
  school: SchoolCode,
  receiptNumber: string
): Promise<{ exists: boolean; receipt_number: string }> => {
  const res = await api.get(`/api/fees/payments/check-receipt/${encodeURIComponent(receiptNumber)}`, {
    params: { school },
  });
  return res.data;
};

export const fetchFeeReport = async (params?: {
  school?: string;
  academic_year?: string;
  term?: number;
}): Promise<FeeReportDto> => {
  const res = await api.get('/api/fees/report', { params });
  return res.data as FeeReportDto;
};

export const initializeFeeAccounts = async (payload: {
  school: string;
  academic_year: string;
  term: number;
}): Promise<{ message: string; total_students: number; new_accounts: number }> => {
  const res = await api.post('/api/fees/initialize-accounts', payload);
  return res.data;
};

export const recalculateTermBalances = async (payload: {
  school: string;
  academic_year: string;
  term: number;
}): Promise<{ message: string; total_students: number }> => {
  const res = await api.post('/api/fees/recalculate-term', payload);
  return res.data;
};

// ─── Report Card Remarks ───────────────────────────────────────────

export const updateReportCardRemarks = async (payload: {
  student_id: number;
  school: string;
  form: number;
  term: number;
  year: number;
  teacher_remarks?: string;
  principal_remarks?: string;
}): Promise<{ message: string }> => {
  const res = await api.put('/api/report-cards/remarks', payload);
  return res.data;
};

// ─── School Settings (Term Management) ──────────────────────────────

export interface SchoolSettingsDto {
  id: number;
  school: string;
  current_academic_year: string;
  current_term: number;
  term_start_date: string | null;
  term_end_date: string | null;
  next_term_opening: string | null;
  boarding_locked: boolean;
}

export const fetchSchoolSettings = async (school: string): Promise<SchoolSettingsDto> => {
  const res = await api.get(`/api/school-settings/${school}`);
  return res.data.settings;
};

export const updateSchoolSettings = async (school: string, data: Partial<Omit<SchoolSettingsDto, 'id' | 'school'>>): Promise<SchoolSettingsDto> => {
  const res = await api.put(`/api/school-settings/${school}`, data);
  return res.data.settings;
};

export const advanceSchoolTerm = async (school: string): Promise<{
  message: string;
  settings: SchoolSettingsDto;
  cleared: { grades: number; assignments: number; graduated?: number; promoted?: number; fee_accounts_opened?: number };
}> => {
  const res = await api.post(`/api/school-settings/${school}/advance-term`);
  return res.data;
};

export const exportTermData = async (school: string): Promise<any> => {
  const res = await api.get(`/api/school-settings/${school}/term-export`);
  return res.data;
};

// ─── Admin Boarding Status Management ───────────────────────────────

export const adminSetBoardingStatus = async (data: {
  student_id: number;
  academic_year: string;
  term: number;
  boarding_status: 'day_scholar' | 'boarder';
}): Promise<{ profile: any }> => {
  const res = await api.post('/api/admin/students/set-boarding-status', data);
  return res.data;
};

export const adminBulkSetBoardingStatus = async (data: {
  form?: number;
  school?: string;
  class_id?: number;
  academic_year: string;
  term: number;
  boarding_status: 'day_scholar' | 'boarder';
}): Promise<{ message: string; updated: number }> => {
  const res = await api.post('/api/admin/students/bulk-set-boarding-status', data);
  return res.data;
};

export interface AdminBoardingStatusEntryDto {
  boarding_status: 'day_scholar' | 'boarder';
  admin_override: boolean;
  updated_at: string;
}

export interface AdminBoardingStatusMapDto {
  school: string;
  academic_year: string;
  term: number;
  statuses: Record<string, AdminBoardingStatusEntryDto>;
}

export const fetchAdminBoardingStatuses = async (params: {
  school: string;
  academic_year: string;
  term: number;
}): Promise<AdminBoardingStatusMapDto> => {
  const res = await api.get('/api/admin/students/boarding-statuses', { params });
  return res.data;
};

export const adminClearBoardingStatuses = async (data: {
  school: string;
  academic_year: string;
  term: number;
  student_ids: number[];
}): Promise<{ message: string; cleared: number }> => {
  const res = await api.post('/api/admin/students/clear-boarding-statuses', data);
  return res.data;
};

export interface FeeChargeDto {
  id: number;
  school: string;
  academic_year: string;
  term: number;
  title: string;
  amount: string;
  description: string | null;
  target_scope: 'single' | 'multiple' | 'all';
  forms: number[] | null;
  boarding_filter: 'boarder' | 'day_scholar' | 'both';
}

export const fetchFeeCharges = async (params: {
  school: string;
  academic_year: string;
  term: number;
}): Promise<FeeChargeDto[]> => {
  const res = await api.get('/api/fees/charges', { params });
  return res.data as FeeChargeDto[];
};

export const createFeeCharge = async (payload: {
  school: string;
  academic_year: string;
  term: number;
  title: string;
  amount: number;
  description?: string;
  target_scope: 'single' | 'multiple' | 'all';
  form?: number;
  forms?: number[];
  boarding_filter: 'boarder' | 'day_scholar' | 'both';
}): Promise<{ charge: FeeChargeDto; students_updated: number }> => {
  const res = await api.post('/api/fees/charges', payload);
  return res.data;
};

export const deleteFeeCharge = async (id: number): Promise<void> => {
  await api.delete(`/api/fees/charges/${id}`);
};

export interface StudentImportPreview {
  school: string;
  unused_columns: string[];
  missing_headers: string[];
  ready_count: number;
  rows: Array<{
    line: number;
    data: {
      first_name?: string;
      last_name?: string;
      gender?: string;
      date_of_birth?: string;
      form?: string;
      boarding_status?: string;
    };
    problems: string[];
    duplicate: boolean;
    ready: boolean;
  }>;
}

export const previewStudentImport = async (school: SchoolCode, file: File): Promise<StudentImportPreview> => {
  const body = new FormData();
  body.append('school', school);
  body.append('file', file);
  const res = await api.post('/api/admin/students/import/preview', body);
  return res.data as StudentImportPreview;
};

export const commitStudentImport = async (
  school: SchoolCode,
  rows: Array<{
    first_name: string;
    last_name: string;
    gender: 'male' | 'female' | 'other';
    date_of_birth: string;
    form: number;
    boarding_status?: string;
  }>
): Promise<{ created_count: number; failed_count: number; created: Array<{ name: string; login_id: string }>; failed: Array<{ index: number; error: string }> }> => {
  const res = await api.post('/api/admin/students/import', { school, rows });
  return res.data;
};

export interface DemoBranding {
  school: SchoolCode;
  display_name: string | null;
  logo_url: string | null;
}

export const enterDemoMode = async (): Promise<{ demo_mode: boolean; branding: DemoBranding[] }> => {
  const res = await api.post('/api/dev/demo/enter');
  return res.data;
};

export const exitDemoMode = async (): Promise<{ demo_mode: boolean }> => {
  const res = await api.post('/api/dev/demo/exit');
  return res.data;
};

export const resetDemoMode = async (): Promise<{ demo_mode: boolean; message: string; branding: DemoBranding[] }> => {
  const res = await api.post('/api/dev/demo/reset');
  return res.data;
};

export const updateDemoBranding = async (payload: {
  school: SchoolCode;
  display_name: string;
  logo?: File | null;
}): Promise<{ branding: DemoBranding[] }> => {
  const body = new FormData();
  body.append('school', payload.school);
  body.append('display_name', payload.display_name);
  if (payload.logo) {
    body.append('logo', payload.logo);
  }
  const res = await api.post('/api/dev/demo/branding', body);
  return res.data;
};

